// eslint-disable-next-line deprecate/import
import Link from 'next/link';

import { useState } from 'react';

import classNames from 'classnames';

import BinanceLogo from '@uikit/icons/BinanceLogo';
import Checked from '@uikit/icons/Checked';
import CloseCircle from '@uikit/icons/CloseCircle';
import type {
  ModalProps} from '@uikit/index';
import {
  ExternalLink,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeading,
  Spinner,
} from '@uikit/index';

import { LAUNCHPAD_INTERNAL_ROUTES } from '@shared/constants';
import { nextProjectUrl } from '@shared/constants/urls';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import useTmaRestrictionModal from '@shared/hooks/useTmaRestrictionModal';

import { useGetKYCBabtMutation } from '@query/user';

type Status = 'success' | 'fail' | 'waiting';

interface KycRequiredModalProps extends Omit<ModalProps, 'children'> {
  isDisabledSumSub?: boolean;
  isDisabledBinance?: boolean;
  onSuccess?: () => void;
}

export const KycRequiredModal = (props: KycRequiredModalProps) => {
  const { mutateAsync: updateBabt } = useGetKYCBabtMutation();
  const [status, setStatus] = useState<Status>('waiting');
  const [loading, setLoading] = useState(false);

  const { isAppOpenedInTelegram, tmaRestrictionModal } = useTmaRestrictionModal();

  const closeModal = () => {
    props.onOpenChange?.(false);
  };

  const handleBinanceVerify = async () => {
    if (isAppOpenedInTelegram) {
      return tmaRestrictionModal.open();
    }

    setLoading(true);

    try {
      const result = await updateBabt();
      setStatus(result.hasBABT ? 'success' : 'fail');
    } catch (error) {
      if (error) {
        setStatus('fail');
        displayErrorToast({ error });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal {...props}>
      <ModalContent className="flex flex-col items-center py-6 px-4 sm:px-12 w-full max-w-[472px]">
        <ModalCloseButton className="!mb-3" />

        <ModalHeading className="mb-4">KYC Verification Required</ModalHeading>

        <p className="text-neutral-400 mb-8 text-center">
          To join the Magic Launchpad IDOs, please verify your identity using Binance KYC.
        </p>

        {status === 'waiting' && (
          <div className="mb-6 bg-secondary-50 py-4 px-2 w-full rounded-lg">
            <ul className="flex flex-col gap-4">
              <li className="flex justify-start items-start gap-1">
                <p>
                  Use the Binance KYC feature by minting a Binance Account Bound (BAB) token. For
                  more information,{' '}
                  <ExternalLink
                    className="text-primary-700 underline"
                    href="https://www.binance.com/en/support/faq/how-to-mint-binance-account-bound-bab-token-bacaf9595b52440ea2b023195ba4a09c"
                  >
                    click here
                  </ExternalLink>
                  .
                </p>
              </li>
            </ul>
          </div>
        )}

        {status === 'success' && (
          <div className="mb-6 bg-green-50 p-4 w-full rounded-lg">
            <p className="font-semibold leading-[52px] items-center justify-start gap-2">
              <Checked className="icon-sm text-green-800" /> Binance KYC Passed
            </p>
          </div>
        )}

        {status === 'fail' && (
          <div className="mb-6 bg-red-200 p-4 w-full rounded-lg flex gap-4">
            <CloseCircle className="icon-sm text-red-600 self-center" />
            <p className="">
              Binance Verification Failed. BAB token not found in your wallet.{' '}
              <ExternalLink
                className="text-primary-700 underline inline-flex"
                href="https://www.binance.com/en/support/faq/how-to-mint-binance-account-bound-bab-token-bacaf9595b52440ea2b023195ba4a09c"
              >
                Learn&nbsp;more
              </ExternalLink>
            </p>
          </div>
        )}

        <div className="flex flex-col gap-4 w-full">
          {status !== 'success' && (
            <button
              className={classNames(
                'button button-solid-secondary button-lg w-full flex justify-center items-center gap-2',
                {
                  'bg-[#F9BA35] hover:bg-[#f9bb35cb] text-neutral-50':
                    !props?.isDisabledBinance && !loading,
                },
              )}
              onClick={handleBinanceVerify}
              disabled={loading || !!props?.isDisabledBinance}
            >
              <BinanceLogo className="text-neutral-50 icon-sm" />{' '}
              {status === 'fail' ? 'Binance Verification Failed, Retry' : 'Verify with Binance'}{' '}
              {loading && <Spinner className="h-4 w-4" />}
            </button>
          )}
          {status === 'success' && (
            <Link
              className="button button-solid-secondary button-lg w-full"
              href={nextProjectUrl.launchpad(LAUNCHPAD_INTERNAL_ROUTES.tokenSales)}
              onClick={closeModal}
            >
              Explore IDOs
            </Link>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default KycRequiredModal;
